import React, { useEffect, useMemo, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { getImoDcsReportSummary } from "../../../../../../services/ChildReportService";
import { ROLE_TYPES } from "../../../../../../constants/roles";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import Loader from "../../../../../../common/Loader/Loader";
import { ToolTip } from "abs-ui-components";
import { CII_UNSATISFACTORY_WARNING_MESSAGES } from "../../../../../../constants/CIIUnsatisfactoryConstants";

// Column Component
const SummaryColumn = ({ column,row }) => {
	const columnStyles = {
		border: column.isBordered ? "1px solid #ccc" : "none",
		textAlign: column.isRightAligned
			? "right"
			: column.isCenterAligned
			? "center"
			: "left",
		width: column.isFixSize ? "300px" : "180px",
		padding: "8px",
		fontWeight: column.isTitle || column.isBold ? "bold" : "normal",
		color: column.isBlue ? "#033dfc" : "inherit",
	};

	const isNumber = !isNaN(Number(column.label));
	const formattedValue =
		isNumber && column.isRightAligned
			? Number(column.label).toFixed(3)
			: column.label;

	 return (
			<td style={columnStyles}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: column.isBlue ? "space-between" : column.isRightAligned ? "flex-end" : column.isCenterAligned ? "center" : "flex-start",
						width: "100%",
					}}
				>
					{column.isBlue && (
						<Tooltip
							title={"Capacity has been changed."}
							arrow
							componentsProps={{
								tooltip: {
									sx: {
										fontSize: "13px",
										backgroundColor: "#012c51 !important",
										color: "white",
									},
								},
							}}
						>
							<InfoIcon
								fontSize="small"
								style={{
									color: "#2964a8",
									cursor: "pointer",
									flexShrink: 0,
								}}
							/>
						</Tooltip>
					)}
					{column.isTitle && row.bdnId ? (
						<Tooltip title={`BDN ID: ${row.bdnId}`} placement="bottom" arrow>
							<span>{formattedValue}</span>
						</Tooltip>
					) : (
						<span>{formattedValue}</span>
					)}
				</div>
			</td>
		);
};

// Row Component
const SummaryRow = ({ row }) => {
	return (
		<tr>
			{row.summaryColumns.map((column, index) => (				
				<SummaryColumn key={index} column={column} row={row}/>
			))}
		</tr>
	);
};

const SummaryTable = ({ table, collapsed }) => {
	const [isCollapsed, setIsCollapsed] = useState(collapsed); // State to toggle table visibility

	const toggleCollapse = () => {
		setIsCollapsed(!isCollapsed);
	};

	// Calculate the maximum number of columns in the table
	const maxColumns = Math.max(
		...table.summaryRows.map((row) => row.summaryColumns.length)
	);

	// Determine the table width based on the number of columns
	const tableWidth =
		maxColumns <= 3 ? "40%" : maxColumns <= 5 ? "60%" : "90%";

	return (
		<div style={{ margin: "20px 0" }}>
			<h5
				onClick={toggleCollapse}
				style={{
					marginLeft: "20px",
					backgroundColor: "#012c51",
					color: "white",
					textTransform: "uppercase",
					padding: "10px",
					cursor: "pointer",
				}}
			>
				{isCollapsed ? "+" : "-"} {table.tableName}
			</h5>
			{!isCollapsed && (
				<>
				<table
					style={{
						width: tableWidth,
						borderCollapse: "collapse",
						marginLeft: "20px",
					}}
				>
						<tbody>
							{table.summaryRows.map((row, index) => (
								<React.Fragment key ={index}>
									<SummaryRow row={row} />

									{table.tableName === "CII Summary" &&
										table.shouldShowUnsatisfactoryWarning &&
										index === 6 && (
											<tr>
												<td colSpan={maxColumns}>
													<div className="unsatifactory-warning-msg" style={{marginBottom:"-10px"}}>
														{CII_UNSATISFACTORY_WARNING_MESSAGES.REPORT_SUMMARY_UNSATISFACTORY_WARNING}
													</div>
												</td>
											</tr>
										)}
								</React.Fragment>
							))}
						</tbody>
				</table>
				</>
			)}
		</div>
	);
};

// Main Component
const DcsSummaryTables = ({ summaryTables }) => {
	return (
		<div>
			{summaryTables.map((table, index) => (
				<SummaryTable key={index} table={table} collapsed={index > 0} />
			))}
		</div>
	);
};

const DcsSummaryView = () => {
	const {
		selectedReportDetails: reportDetails,
	} = useSelector((state) => state.reports, shallowEqual);
	const user = useSelector((state) => state.userInfo.userData);
	const [loading, setLoading] = useState(true);
	const [summaryTablesData, setSummaryTablesData] = useState([]);

	const isErClient = useMemo(() => {
		const roles = user?.roles ?? user?.Roles ?? [];

		if (!Array.isArray(roles)) {
			return false;
		}

		return roles.some(
			(role) =>
				String(role || "").toUpperCase().trim() ===
				ROLE_TYPES.ER_CLIENT
		);
	}, [user]);

	const summaryTables = useMemo(() => {
		if (!Array.isArray(summaryTablesData)) {
			return [];
		}

		return summaryTablesData.map((table) => {
			if (
				String(table?.tableName || "").toUpperCase().trim() !==
				"CII SUMMARY"
			) {
				return table;
			}

			return {
				...table,
				summaryRows: (table?.summaryRows || []).map((row) => {
					const summaryColumns = (row?.summaryColumns || [])
						.filter((column) => {
							const label = String(column?.label || "")
								.toUpperCase()
								.trim();

							if (!isErClient) {
								return true;
							}

							return label !== "WITH CII EXCLUSION";
						})
						.map((column) => {
							const label = String(column?.label || "")
								.toUpperCase()
								.trim();

							if (label === "WITHOUT CII EXCLUSION") {
								return {
									...column,
									label: "Reported",
								};
							}

							return column;
						});

					return {
						...row,
						summaryColumns,
					};
				}),
			};
		});
	}, [isErClient, summaryTablesData]);

  useEffect(() => {
    const fetchDcsmrvReportSummary = async () => {
      const reportMstId = reportDetails?.ReportMstId;
      if (reportMstId) {
        try {
          const dcsReportSummaryData = await getImoDcsReportSummary(reportMstId);
          setSummaryTablesData(dcsReportSummaryData.data.summaryTables)
          setLoading(false);
        } catch (error) {
          console.error("Error fetching report summary:", error);
          setLoading(false);
        }
      } else {
        console.warn("ReportMstId is missing or undefined.");
        setLoading(false);
      }
    };
    fetchDcsmrvReportSummary();
  }, [reportDetails]);

	return (
		<>
			<div>
				<div>
					{loading ? (
						<div className="loader">
							<Loader />
						</div>
					) : (
						<div>
							<DcsSummaryTables summaryTables={summaryTables} />
						</div>
					)}
				</div>
			</div>
		</>
	);
};
export default DcsSummaryView;
